import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

// Custom hook for Supabase data fetching
export const useSupabaseData = () => {
  const [data, setData] = useState({
    clients: [],
    pages: [],
    employees: [],
    namingFiles: [],
    loading: true,
    error: null
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      console.log('🔵 fetchAllData - Starting data fetch');

      // Fetch clients, pages, employees, and naming_files in parallel
        const [clientsResponse, pagesResponse, employeesResponse, namingFilesResponse] = await Promise.all([
          supabase.from('clients').select('code').order('code'),
          supabase.from('pages').select('page'),
          supabase.from('users').select('user_code, full_name').order('full_name'),
          supabase.from('naming_files').select('*').order('created_at', { ascending: false })
        ]);

      // Check for errors
      if (clientsResponse.error) throw clientsResponse.error;
      if (pagesResponse.error) throw pagesResponse.error;
      if (employeesResponse.error) throw employeesResponse.error;
      if (namingFilesResponse.error) throw namingFilesResponse.error;

      // Extract the data
      const clients = clientsResponse.data.map(item => item.code);
      const pages = pagesResponse.data.map(item => item.page);
      const employees = employeesResponse.data.map(item => item.full_name);
      const employeeMapping = employeesResponse.data.reduce((acc, item) => {
        acc[item.full_name] = item.user_code;
        return acc;
      }, {});
      const namingFiles = namingFilesResponse.data || [];

      setData({
        clients,
        pages,
        employees,
        employeeMapping,
        namingFiles,
        loading: false,
        error: null
      });

      console.log('🟢 fetchAllData - Success, loaded', namingFiles.length, 'naming files');

    } catch (error) {
      console.error('🔴 fetchAllData - Error fetching data:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  const refetch = () => {
    fetchAllData();
  };

  return { ...data, refetch };
};

// Custom hook for database operations
export const useDatabaseOperations = () => {
  const insertRowToDatabase = async (row) => {
    try {
      // Get current authenticated user for attribution
      const session = (await supabase.auth.getSession()).data.session;
      const currentUserId = session?.user?.id || null; // If null, created_by will be null (RLS may reject if required)
      const dbRecord = {
        code: row.code,
        devices: row.device,
        client: row.client,
        page: row.page,
        section: row.section,
        first: row.first,
        last: row.last,
        usage: row.usage,
        description: row.description,
        width: row.width || null, // Convert empty string to null for numeric fields
        height: row.height || null, // Convert empty string to null for numeric fields
        date: row.date || null, // Convert empty string to null for date field
        employee_name: row.employee || null, // Store full_name in employee_name column
        file_name: row.fileName,
        drive_url: row.driveLink || null, // Add drive URL
        created_at: row.created,
        updated_at: row.updated
        // Note: user_id will be automatically assigned by RLS trigger
        , created_by: currentUserId
      };

      const { data, error } = await supabase
        .from('naming_files')
        .insert([dbRecord])
        .select()
        .single();

      if (error) {
        console.error('🔴 insertRowToDatabase - Supabase error:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('🔴 insertRowToDatabase - Caught error:', error);
      throw error;
    }
  };

  const updateRowInDatabase = async (row) => {
    try {
      console.log('🔵 updateRowInDatabase - Starting update for row:', row.id);
      
      const dbRecord = {
        code: row.code,
        devices: row.device,
        client: row.client,
        page: row.page,
        section: row.section,
        first: row.first,
        last: row.last,
        usage: row.usage,
        description: row.description,
        width: row.width || null, // Convert empty string to null for numeric fields
        height: row.height || null, // Convert empty string to null for numeric fields
        date: row.date || null, // Convert empty string to null for date field
        employee_name: row.employee || null, // Store full_name in employee_name column
        file_name: row.fileName,
        drive_url: row.driveLink || null, // Add drive URL
        updated_at: row.updated
        // Note: user_id will be automatically assigned by RLS trigger
      };
      // Ensure created_by is never overwritten after initial insert
      if ('created_by' in row) {
        // Do not include created_by in update payload
      }

      // First check if the row exists in the database
      const { error: checkError } = await supabase
        .from('naming_files')
        .select('id')
        .eq('id', row.id)
        .single();

      if (checkError && checkError.code === 'PGRST116') {
        console.log('🟡 updateRowInDatabase - Row does not exist, inserting instead');
        // Row doesn't exist, insert it instead
        return await insertRowToDatabase(row);
      } else if (checkError) {
        throw checkError;
      }

      // Row exists, proceed with update
      const { data, error } = await supabase
        .from('naming_files')
        .update(dbRecord)
        .eq('id', row.id)
        .select()
        .single();

      if (error) {
        console.error('🔴 updateRowInDatabase - Supabase error:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('🔴 updateRowInDatabase - Caught error:', error);
      throw error;
    }
  };

  const deleteRowFromDatabase = async (id) => {
    try {
      const { error } = await supabase
        .from('naming_files')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('🔴 deleteRowFromDatabase - Error:', error);
      throw error;
    }
  };

  return {
    insertRowToDatabase,
    updateRowInDatabase,
    deleteRowFromDatabase
  };
};